import { BankAccountNumberInput } from '@/_components/inputs/bank-account-number-input'
import { CnpjInput } from '@/_components/inputs/cnpj-input'
import { PhoneInput } from '@/_components/inputs/phone-input'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/_components/ui'
import { ContentContainer } from '@/features/_components/content-container'
import { InputGroup } from '@/features/_components/input-group'
import { Text } from '@/features/_components/ui/typography'
import { ApiQueryError, mutations } from '@/features/_lib/react-query'
import type { TExposedFacilityDTO } from '@doutorsim/shared/facilities'
import { Title } from '@radix-ui/react-dialog'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Pencil, X } from 'lucide-react'
import { useContext, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { CiCircleAlert } from 'react-icons/ci'
import { toast } from 'sonner'
import { BankSelect } from '../../_components/bank-select'
import { EditContext } from '../_providers/edit'
import type { TFacilitySettingsFormValues } from '../_providers/form-provider'

const PIX_KEY_TYPE_OPTIONS = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'evp', label: 'Chave aleatória' },
] as const

type FacilitySettingsFormProps = {
  facility: TExposedFacilityDTO
  canEdit?: boolean
}

export const FacilitySettingsForm = ({ facility, canEdit = false }: FacilitySettingsFormProps) => {
  const queryClient = useQueryClient()
  const updateFacility = useMutation(mutations.companies.updateFacility(queryClient))

  const isAdmin = canEdit

  const form = useFormContext<TFacilitySettingsFormValues>()
  const { isFormEditable, isCancelDialogOpen, setIsCancelDialogOpen, setIsFormEditable } =
    useContext(EditContext)

  const formWasModified = Object.keys(form.formState.dirtyFields).length > 0

  const handleCancelClick = () => setIsCancelDialogOpen(true)

  const formRef = useRef<HTMLFormElement | null>(null)

  const buttonDisabledStates = !formWasModified || form.formState.isSubmitting || !isFormEditable

  const resetForm = () => {
    form.reset()
    setIsCancelDialogOpen(false)
    setIsFormEditable(false)
  }

  const onSubmit = (formData: TFacilitySettingsFormValues) => {
    const hasBankData = formData.bankCode && formData.branchNumber && formData.accountNumber

    const bankAccount = hasBankData
      ? {
          ...(facility.bankAccount?.id ? { id: facility.bankAccount.id } : {}),
          bankCode: formData.bankCode!,
          branchNumber: formData.branchNumber!,
          accountNumber: formData.accountNumber!,
          legalName: null,
          pixKey: formData.pixKey ?? null,
          pixKeyType: formData.pixKey ? (formData.pixKeyType ?? null) : null,
        }
      : null

    updateFacility.mutate(
      {
        id: facility.id,
        companyId: facility.companyId,
        name: formData.name,
        legalName: formData.legalName,
        cnpj: formData.cnpj,
        address: facility.address!,
        contactInfo: {
          phone: formData.phone,
          email: formData.email,
        },
        bankAccount,
      },
      {
        onSuccess: () => {
          setIsFormEditable(false)
          formRef.current?.scrollIntoView({ behavior: 'smooth' })
          form.reset(formData)
          toast.success('Unidade atualizada com sucesso!')
        },
        onError: (error) => {
          console.error(error)
          toast.error('Erro ao atualizar a unidade', {
            description:
              error instanceof ApiQueryError
                ? (error.apiError.detail ?? error.message)
                : 'Erro inesperado',
          })
        },
      },
    )
  }

  return (
    <div className="scrollbar w-full overflow-auto pr-5">
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent className="min-w-[336px] max-w-[389px] p-5">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-1">
                <CiCircleAlert size={24} />
                <Title className="text-base">Há alterações não salvas!</Title>
              </div>
              <Text>Ao prosseguir, essas alterações serão perdidas. Deseja continuar?</Text>
            </div>

            <div className="flex gap-3 self-end">
              <DialogClose asChild>
                <Button variant="gray" className="rounded-md font-bold">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                className="min-w-[80px] text-sm font-bold"
                onClick={resetForm}
              >
                Continuar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Form {...form}>
        <form
          className="flex w-full flex-col gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
          ref={formRef}
        >
          <ContentContainer>
            <div className="flex items-center justify-between">
              <h2 className="font-bold">Identificação</h2>
              {isAdmin && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    if (isFormEditable && formWasModified) {
                      return setIsCancelDialogOpen(true)
                    }
                    setIsFormEditable(!isFormEditable)
                  }}
                >
                  {isFormEditable ? (
                    <>
                      <X size={12} />
                      Cancelar
                    </>
                  ) : (
                    <>
                      <Pencil size={12} />
                      Editar
                    </>
                  )}
                </button>
              )}
            </div>
            <InputGroup>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Nome Fantasia</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Digite o nome fantasia da unidade"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>

            <InputGroup>
              <FormField
                control={form.control}
                name="legalName"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Razão Social</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Digite a razão social"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>

            <InputGroup>
              <FormField
                control={form.control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">CNPJ</FormLabel>
                    <FormControl>
                      <CnpjInput
                        placeholder="Digite o CNPJ da unidade"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>
          </ContentContainer>

          <ContentContainer>
            <h2 className="font-bold">Contato</h2>
            <InputGroup>
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Telefone</FormLabel>
                    <FormControl>
                      <PhoneInput
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">E-mail</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Digite o e-mail da unidade"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>
          </ContentContainer>

          <ContentContainer>
            <h2 className="font-bold">Dados bancários</h2>
            <InputGroup>
              <FormField
                control={form.control}
                name="bankCode"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Banco</FormLabel>
                    <BankSelect
                      selectedBankCode={field.value ?? undefined}
                      onChange={field.onChange}
                      disabled={!isAdmin || !isFormEditable}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="branchNumber"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Agência</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="0000"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>

            <InputGroup>
              <FormField
                control={form.control}
                name="accountNumber"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Conta corrente</FormLabel>
                    <FormControl>
                      <BankAccountNumberInput
                        placeholder="0000000-0"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="pixKeyType"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Tipo da chave PIX</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? undefined}
                      disabled={!isAdmin || !isFormEditable}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo da chave" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PIX_KEY_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>

            <InputGroup>
              <FormField
                control={form.control}
                name="pixKey"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Chave PIX</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Digite a chave PIX"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>
          </ContentContainer>

          <p className="flex h-1 items-center px-5 text-sm opacity-50">
            Criada em{' '}
            {facility.createdAt
              ? new Date(facility.createdAt).toLocaleDateString('pt-BR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : '-'}
          </p>
          {isAdmin && (
            <div className="flex justify-end">
              <Button
                variant="gray"
                type="button"
                disabled={buttonDisabledStates}
                onClick={handleCancelClick}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={buttonDisabledStates}>
                {updateFacility.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  )
}
